/**
 * blogSerializer.js
 * Converts between the legacy sections array (backend format) and Tiptap JSON.
 *
 * Inline formatting (bold, italic, code, links, strike) is preserved via an
 * `inlineContent` field — a verbatim copy of the Tiptap inline-node array.
 * Old backend records that lack `inlineContent` fall back to the plain-text
 * `value` field, so the format is fully backward-compatible.
 */

import { detectVideoFromUrl } from './VideoEmbed';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Recursively extract plain text from a Tiptap node (for the `value` field) */
function extractText(node) {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (!node.content) return '';
  return node.content.map(extractText).join('');
}

/**
 * Return the inline-node array of a block node (paragraph / heading / listItem
 * paragraph). This is stored verbatim as `inlineContent` in the section so that
 * all marks (bold, italic, code, link, strike) survive the round-trip.
 *
 * Returns undefined (not stored) when the node has no inline children.
 */
function extractInlineContent(blockNode) {
  if (!blockNode?.content?.length) return undefined;
  return blockNode.content; // array of { type:'text', text, marks? } | { type:'hardBreak' }
}

/** Build a Tiptap table node from a rows: string[][] */
function buildTableNode(rows = []) {
  if (!rows.length) return null;

  const tableRows = rows.map((row, rowIndex) => ({
    type: 'tableRow',
    content: row.map((cellText) => ({
      type: rowIndex === 0 ? 'tableHeader' : 'tableCell',
      attrs: { colspan: 1, rowspan: 1, colwidth: null },
      content: [
        {
          type: 'paragraph',
          content: cellText ? [{ type: 'text', text: cellText }] : [],
        },
      ],
    })),
  }));

  return { type: 'table', content: tableRows };
}

/**
 * Build the content array for a single Tiptap paragraph from a section field.
 * Prefers `inlineContent` (marks-aware), falls back to plain-text `value`.
 */
function inlineNodesFor(section) {
  if (Array.isArray(section.inlineContent) && section.inlineContent.length > 0) {
    return section.inlineContent;
  }
  return section.value ? [{ type: 'text', text: section.value }] : [];
}

/** Flatten nested Tiptap list into legacy items array with level numbers */
function flattenListItems(listNode, level = 0) {
  const items = [];
  if (!listNode.content) return items;

  for (const listItem of listNode.content) {
    if (listItem.type !== 'listItem') continue;

    let text = '';
    let inlineContent;

    for (const child of listItem.content ?? []) {
      if (child.type === 'paragraph') {
        text = extractText(child);
        inlineContent = extractInlineContent(child);
      } else if (child.type === 'bulletList' || child.type === 'orderedList') {
        // Nested list — push current item first, then recurse
        const nested = flattenListItems(child, level + 1);
        items.push({ id: `item-${Date.now()}-${Math.random()}`, text, inlineContent, level });
        items.push(...nested);
        text = null; // already pushed
        inlineContent = null;
      }
    }

    if (text !== null) {
      items.push({ id: `item-${Date.now()}-${Math.random()}`, text, inlineContent, level });
    }
  }

  return items;
}

/** Build a Tiptap bulletList or orderedList from a legacy list section */
function buildListNode(section) {
  const { items = [], listStyle = 'bullet' } = section;
  const isBullet = listStyle === 'bullet';
  const listType = isBullet ? 'bulletList' : 'orderedList';

  function buildItems(flatItems, depth = 0) {
    const result = [];
    let i = 0;
    while (i < flatItems.length) {
      const item = flatItems[i];
      if (item.level !== depth) { i++; continue; }

      // Collect children (level = depth+1) that immediately follow
      const children = [];
      let j = i + 1;
      while (j < flatItems.length && flatItems[j].level > depth) {
        children.push(flatItems[j]);
        j++;
      }

      // Prefer inlineContent for marks; fall back to plain text
      const paragraphContent =
        Array.isArray(item.inlineContent) && item.inlineContent.length > 0
          ? item.inlineContent
          : item.text
          ? [{ type: 'text', text: item.text }]
          : [];

      const listItemContent = [
        { type: 'paragraph', content: paragraphContent },
      ];

      if (children.length > 0) {
        listItemContent.push({
          type: listType,
          ...(listStyle !== 'bullet' ? { attrs: { listStyle } } : {}),
          content: buildItems(children, depth + 1),
        });
      }

      result.push({ type: 'listItem', content: listItemContent });
      i = j;
    }
    return result;
  }

  const listItemNodes = buildItems(items, 0);

  return {
    type: listType,
    ...(listStyle !== 'bullet' ? { attrs: { listStyle } } : {}),
    content: listItemNodes.length > 0
      ? listItemNodes
      : [{ type: 'listItem', content: [{ type: 'paragraph' }] }],
  };
}

// ─── Deserialization: sections → Tiptap JSON ────────────────────────────────

/**
 * Convert a legacy sections array to a Tiptap-compatible JSON document.
 * Handles both old plain-text records and new records with `inlineContent`.
 * @param {Array} sections
 * @returns {{ type: 'doc', content: Array }}
 */
export function sectionsToTiptapJSON(sections = []) {
  const content = [];

  for (const section of sections) {
    if (!section) continue;

    switch (section.type) {
      case 'heading':
      case 'subHeading': {
        const level =
          section.type === 'subHeading'
            ? 3
            : Math.min(6, Math.max(1, Number(section.headingLevel) ?? 2));
        content.push({
          type: 'heading',
          attrs: { level },
          content: inlineNodesFor(section),
        });
        break;
      }

      case 'paragraph': {
        content.push({
          type: 'paragraph',
          content: inlineNodesFor(section),
        });
        break;
      }

      case 'codeBlock': {
        content.push({
          type: 'codeBlock',
          attrs: { language: section.language ?? '' },
          content: section.value ? [{ type: 'text', text: section.value }] : [],
        });
        break;
      }

      case 'horizontalRule': {
        content.push({ type: 'horizontalRule' });
        break;
      }

      case 'image': {
        if (section.imageUrl) {
          content.push({
            type: 'image',
            attrs: { src: section.imageUrl, alt: section.alt ?? '', title: null },
          });
        }
        break;
      }

      case 'table': {
        const tableNode = buildTableNode(section.rows);
        if (tableNode) content.push(tableNode);
        break;
      }

      case 'list': {
        content.push(buildListNode(section));
        break;
      }

      case 'video': {
        const videoUrl = section.videoUrl || '';
        if (videoUrl) {
          const detected = detectVideoFromUrl(videoUrl);
          if (detected) {
            content.push({
              type: 'videoEmbed',
              attrs: {
                videoType: detected.videoType,
                videoId: detected.videoId,
                url: videoUrl,
              },
            });
          }
        }
        break;
      }

      default:
        break;
    }
  }

  // Tiptap requires at least one block node
  if (content.length === 0) content.push({ type: 'paragraph' });

  return { type: 'doc', content };
}

// ─── Serialization: Tiptap JSON → sections ──────────────────────────────────

/** Extract plain text rows from a Tiptap table node */
function extractTableRows(tableNode) {
  const rows = [];
  if (!tableNode.content) return rows;
  for (const rowNode of tableNode.content) {
    if (rowNode.type !== 'tableRow') continue;
    const row = [];
    for (const cellNode of rowNode.content ?? []) {
      row.push(extractText(cellNode));
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Convert a Tiptap JSON document back to a sections array for the backend.
 * Each text-bearing section gets:
 *   - `value`         — plain text (legacy field, always present)
 *   - `inlineContent` — verbatim Tiptap inline nodes with marks (new field)
 *
 * The backend stores both; the preview uses `inlineContent` when available,
 * falling back to `value` for old records.
 *
 * @param {{ type: 'doc', content: Array }} tiptapJson
 * @returns {Array}
 */
export function tiptapJSONToSections(tiptapJson) {
  if (!tiptapJson || !Array.isArray(tiptapJson.content)) return [];
  const sections = [];

  for (const node of tiptapJson.content) {
    if (!node) continue;

    switch (node.type) {
      case 'heading': {
        const level = node.attrs?.level ?? 2;
        const inlineContent = extractInlineContent(node);
        sections.push({
          type: 'heading',
          value: extractText(node),
          headingLevel: level,
          ...(inlineContent ? { inlineContent } : {}),
        });
        break;
      }

      case 'paragraph': {
        const text = extractText(node);
        const inlineContent = extractInlineContent(node);
        // Skip completely empty leading paragraphs
        if (text.trim() || sections.length > 0 || inlineContent) {
          sections.push({
            type: 'paragraph',
            value: text,
            ...(inlineContent ? { inlineContent } : {}),
          });
        }
        break;
      }

      case 'codeBlock': {
        sections.push({
          type: 'codeBlock',
          value: extractText(node),
          language: node.attrs?.language ?? '',
        });
        break;
      }

      case 'horizontalRule': {
        sections.push({ type: 'horizontalRule' });
        break;
      }

      case 'image': {
        const src = node.attrs?.src ?? '';
        if (src) {
          sections.push({
            type: 'image',
            imageUrl: src,
            alt: node.attrs?.alt ?? '',
            id: `img-${Date.now()}-${Math.random()}`,
          });
        }
        break;
      }

      case 'table': {
        sections.push({
          type: 'table',
          rows: extractTableRows(node),
        });
        break;
      }

      case 'bulletList': {
        sections.push({
          type: 'list',
          listStyle: 'bullet',
          listStyles: ['bullet'],
          items: flattenListItems(node, 0),
        });
        break;
      }

      case 'orderedList': {
        const listStyle = node.attrs?.listStyle ?? 'numbered';
        sections.push({
          type: 'list',
          listStyle,
          listStyles: [listStyle],
          items: flattenListItems(node, 0),
        });
        break;
      }

      case 'videoEmbed': {
        const { url } = node.attrs ?? {};
        if (url) {
          sections.push({ type: 'video', videoUrl: url });
        }
        break;
      }

      default:
        break;
    }
  }

  return sections;
}
